/**
 * Inventory Widget Utility Functions
 *
 * Shared utility functions for inventory widget data processing.
 * Used by both TableChartWidget (runtime) and WidgetWizard (preview).
 */

/**
 * Valid table layouts per row type
 */
export const INVENTORY_LAYOUTS = {
  hosts: ['long_form', 'split_by_category', 'pivot'],
  items: ['aggregated', 'expanded'],
};

/**
 * Default table layout per row type
 */
export const DEFAULT_LAYOUTS = {
  hosts: 'long_form',
  items: 'aggregated',
};

/**
 * Get a valid table layout based on the row type.
 * Ensures the layout is appropriate for the selected row type.
 *
 * @param {string} layout - The requested table layout
 * @param {string} rowType - The row type ('hosts' or 'items')
 * @returns {string} A valid table layout for the given row type
 */
export const getValidTableLayout = (layout, rowType) => {
  const validLayouts = INVENTORY_LAYOUTS[rowType] || INVENTORY_LAYOUTS.hosts;
  const defaultLayout = DEFAULT_LAYOUTS[rowType] || DEFAULT_LAYOUTS.hosts;

  if (!layout || !validLayouts.includes(layout)) {
    return defaultLayout;
  }
  return layout;
};

/**
 * Build inventory config object from widget config or dataSource config.
 * Normalizes config structure for API calls.
 *
 * @param {Object} config - Widget config (with inventoryXxx fields) or dataSource config
 * @param {boolean} isDataSourceConfig - Whether config is from dataSource.config (no 'inventory' prefix)
 * @returns {Object} Normalized inventory config object
 */
export const buildInventoryConfig = (config, isDataSourceConfig = false) => {
  if (!config) return null;

  // When config comes from dataSource.config, fields don't have 'inventory' prefix
  if (isDataSourceConfig) {
    const rowType = config.rowType || 'hosts';
    const tableLayout = getValidTableLayout(config.tableLayout, rowType);

    return {
      inventoryRowType: rowType,
      inventoryTableLayout: tableLayout,
      inventoryCategoryHandling: config.categoryHandling || 'column',
      inventorySelectedCategories: config.selectedCategories || [],
      inventoryScanBasis: config.scanBasis || 'latest',
      inventoryPivotColumnLimit: config.pivotColumnLimit ?? 20,
    };
  }

  // When config comes from widget.config, fields have 'inventory' prefix
  const rowType = config.inventoryRowType || 'hosts';
  const tableLayout = getValidTableLayout(config.inventoryTableLayout, rowType);

  return {
    inventoryRowType: rowType,
    inventoryTableLayout: tableLayout,
    inventoryCategoryHandling: config.inventoryCategoryHandling || 'column',
    inventorySelectedCategories: config.inventorySelectedCategories || [],
    inventoryScanBasis: config.inventoryScanBasis || 'latest',
    inventoryPivotColumnLimit: config.inventoryPivotColumnLimit ?? 20,
  };
};

/**
 * Flatten split_by_category tables into a single table format.
 * The backend returns tables as { categoryName: { columns, rows, total } }.
 * This function flattens them into rows with Category as the first column.
 *
 * @param {Object} tables - Backend response tables object
 * @returns {Object} Flattened data with columns and rows
 */
export const flattenSplitByCategoryTables = (tables) => {
  if (!tables || typeof tables !== 'object') {
    return { columns: [], rows: [], total: 0 };
  }

  const flatRows = [];
  const columns = ['Category', 'Host', 'Item', 'Value'];

  Object.entries(tables).forEach(([category, tableData]) => {
    if (tableData && Array.isArray(tableData.rows)) {
      tableData.rows.forEach((subRow) => {
        // subRow is [Host, Item, Value], we prepend Category as first column
        // Defensive check: ensure subRow has at least 3 elements
        if (Array.isArray(subRow) && subRow.length >= 3) {
          flatRows.push([category, subRow[0], subRow[1], subRow[2]]);
        }
      });
    }
  });

  return {
    columns,
    rows: flatRows,
    total: flatRows.length,
  };
};
